import { useGetProgramStoresQuery } from '@graphql/generated/graphql';
import logger from 'src/utils/logger';
import { usePageLoaded } from 'src/hooks/usePageLoaded';
import { useEffect } from 'react';

export interface ProgramStore {
  store: string;
  label: string;
}

interface UseGetProgramStoresResult {
  stores: ProgramStore[];
  isLoading: boolean;
}

const useGetProgramStores = (): UseGetProgramStoresResult => {
  const pageLoaded = usePageLoaded();
  const { data, error, isError, isLoading, isFetching } = useGetProgramStoresQuery(
    {},
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: pageLoaded,
    },
  );

  useEffect(() => {
    if (isError) {
      const { message, name } = error as Error;
      void logger.error(`Error occurred while retrieving list of stores: ${name}: ${message}`);
    }
  }, [isError, error]);

  const storeList: ProgramStore[] =
    data?.getProgramStores.stores
      ?.map((store) => ({
        store: store.storeId,
        label: store.storeCode
          ? `${store.storeCode} - ${store.storeName ?? store.storeId}`
          : `${store.storeId}   [${store.warehouseId}]`,
      }))
      .sort((s1: ProgramStore, s2: ProgramStore) => s1.label.localeCompare(s2.label)) || [];

  return {
    stores: storeList,
    isLoading: isLoading || (isFetching && storeList.length === 0),
  };
};

export default useGetProgramStores;
